import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { 
  Activity, 
  Brain, 
  Database, 
  Globe, 
  ShieldCheck, 
  Users, 
  TrendingUp, 
  Layers, 
  Zap, 
  ChevronRight,
  Target,
  Share2,
  Cpu
} from 'lucide-react';

/* --- Components --- */

// 1. 背景动画网格
const BackgroundGrid = () => (
  <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
    <div className="absolute inset-0 bg-slate-950">
      <div className="absolute inset-0 opacity-[0.15]" 
           style={{ 
             backgroundImage: 'linear-gradient(#10b981 1px, transparent 1px), linear-gradient(90deg, #10b981 1px, transparent 1px)', 
             backgroundSize: '40px 40px' 
           }}>
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950"></div>
    </div>
    {/* 动态光晕 */}
    <motion.div 
      animate={{ 
        scale: [1, 1.2, 1],
        opacity: [0.3, 0.5, 0.3],
      }}
      transition={{ duration: 8, repeat: Infinity }}
      className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-[100px]"
    />
    <motion.div 
      animate={{ 
        scale: [1, 1.3, 1],
        opacity: [0.2, 0.4, 0.2],
      }}
      transition={{ duration: 10, repeat: Infinity, delay: 1 }}
      className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-teal-600/10 rounded-full blur-[120px]"
    />
  </div>
);

// 2. 导航栏
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-slate-950/80 backdrop-blur-md border-b border-emerald-500/20 py-4' : 'bg-transparent py-6'}`}>
      <div className="container mx-auto px-6 flex justify-between items-center">
        <div className="flex items-center gap-2">
          {/* Logo 替换为心电图样式 */}
          <div className="w-8 h-8 bg-gradient-to-tr from-emerald-400 to-teal-600 rounded-lg flex items-center justify-center text-slate-950 shadow-[0_0_15px_rgba(16,185,129,0.5)]">
            <Activity className="w-5 h-5 stroke-[3px]" />
          </div>
          <span className="text-xl font-bold tracking-wider text-white">WELLRISE <span className="text-emerald-400 font-light">MEDTECH</span></span>
        </div>
        <div className="hidden md:flex gap-8 text-sm font-medium text-slate-300">
          {['关于我们', '痛点解决', '核心模式', '科技引擎', '投资价值'].map((item) => (
            <a key={item} href={`#${item}`} className="hover:text-emerald-400 transition-colors cursor-pointer uppercase tracking-widest">{item}</a>
          ))}
        </div>
        <button className="bg-emerald-500/10 hover:bg-emerald-500 text-emerald-400 hover:text-slate-950 border border-emerald-500/50 px-6 py-2 rounded-full text-sm transition-all duration-300">
          联系我们
        </button>
      </div>
    </nav>
  );
};

// 3. 英雄区域
const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center z-10 pt-20">
      <div className="container mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
        <motion.div 
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-block px-4 py-1 mb-6 rounded-full bg-emerald-900/30 border border-emerald-500/30 text-emerald-400 text-sm font-mono">
            数字化驱动的临床方案领航者
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight mb-6">
            广东沃瑞<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">医疗科技</span>
          </h1>
          
          {/* --- 核心修改部分：强调增长与效率 --- */}
          <div className="space-y-4 mb-8 max-w-lg">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="flex items-start gap-3"
            >
              <div className="mt-2 w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0 shadow-[0_0_10px_rgba(52,211,153,0.5)]"></div>
              <p className="text-slate-300 leading-relaxed">
                <span className="text-white font-semibold">高速增长：</span>
                业务版图快速扩张，年均增速超行业平均水平
              </p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="flex items-start gap-3"
            >
              <div className="mt-2 w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0 shadow-[0_0_10px_rgba(52,211,153,0.5)]"></div>
              <p className="text-slate-300 leading-relaxed">
                <span className="text-white font-semibold">效能飞跃：</span>
                AI 驱动运营闭环，人均产出效率提升 5 倍
              </p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              className="flex items-start gap-3"
            >
              <div className="mt-2 w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0 shadow-[0_0_10px_rgba(52,211,153,0.5)]"></div>
              <p className="text-slate-300 leading-relaxed">
                <span className="text-white font-semibold">资产裂变：</span>
                数字化资产复利效应，实现可持续高速发展
              </p>
            </motion.div>
          </div>
          {/* --- 修改结束 --- */}

          <div className="flex gap-4">
            <button className="bg-emerald-500 text-slate-950 px-8 py-4 rounded-lg font-bold hover:bg-emerald-400 transition-all flex items-center gap-2 group">
              探索未来医疗 <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button className="border border-slate-600 text-slate-300 px-8 py-4 rounded-lg font-medium hover:border-emerald-500 hover:text-emerald-400 transition-all">
              了解合作模式
            </button>
          </div>
        </motion.div>

        {/* 3D 视觉元素示意 - 更新为动态增长图表 */}
        <div className="relative h-[400px] md:h-[600px] flex items-center justify-center">
          <motion.div 
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute w-[300px] h-[300px] border border-emerald-500/20 rounded-full border-dashed"
          />
          <motion.div 
            animate={{ rotate: -360 }}
            transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
            className="absolute w-[450px] h-[450px] border border-cyan-500/10 rounded-full"
          />
          
          {/* 卡片主体 */}
          <div className="relative z-10 bg-gradient-to-br from-slate-900/90 to-slate-800/90 p-6 rounded-2xl border border-emerald-500/30 shadow-2xl shadow-emerald-500/20 max-w-sm backdrop-blur-md w-full">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_8px_#10b981]"></div>
                <span className="text-emerald-400 font-mono text-xs font-bold tracking-wider">AI 增长引擎启动</span>
              </div>
              <TrendingUp className="w-4 h-4 text-emerald-500 animate-bounce" />
            </div>

            {/* 动态图表区域 */}
            <div className="relative h-32 mb-6 w-full">
               {/* 坐标轴线 */}
               <div className="absolute inset-0 grid grid-cols-6 gap-2 opacity-10 pointer-events-none">
                  <div className="border-r border-slate-400 h-full"></div>
                  <div className="border-r border-slate-400 h-full"></div>
                  <div className="border-r border-slate-400 h-full"></div>
                  <div className="border-r border-slate-400 h-full"></div>
                  <div className="border-r border-slate-400 h-full"></div>
               </div>
               
               {/* SVG 曲线 */}
               <svg viewBox="0 0 100 50" className="w-full h-full overflow-visible">
                 <defs>
                   <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                     <stop offset="0%" stopColor="#10b981" stopOpacity="0.4" />
                     <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                   </linearGradient>
                 </defs>
                 {/* 填充区域 */}
                 <motion.path 
                   d="M0 45 C 20 40, 40 45, 50 30 S 70 10, 100 5 L 100 50 L 0 50 Z" 
                   fill="url(#chartGradient)"
                   initial={{ opacity: 0 }}
                   animate={{ opacity: 1 }}
                   transition={{ duration: 1.5, delay: 0.5 }}
                 />
                 {/* 线条路径 */}
                 <motion.path
                   d="M0 45 C 20 40, 40 45, 50 30 S 70 10, 100 5"
                   fill="none"
                   stroke="#10b981"
                   strokeWidth="2.5"
                   strokeLinecap="round"
                   initial={{ pathLength: 0 }}
                   animate={{ pathLength: 1 }}
                   transition={{ duration: 2, ease: "easeInOut", repeat: Infinity, repeatDelay: 4 }}
                 />
               </svg>
            </div>

            {/* 核心数据展示 */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-800/60 p-3 rounded border border-slate-700/50 hover:border-emerald-500/30 transition-colors">
                <div className="text-xs text-slate-400 mb-1">业务增速 (YoY)</div>
                <div className="flex items-end gap-1">
                  <motion.span 
                    className="text-2xl font-bold text-white"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    +300%
                  </motion.span>
                  <TrendingUp className="w-3 h-3 text-emerald-500 mb-1.5" />
                </div>
              </div>
              <div className="bg-slate-800/60 p-3 rounded border border-slate-700/50 hover:border-emerald-500/30 transition-colors">
                <div className="text-xs text-slate-400 mb-1">运营效能提升</div>
                <div className="flex items-end gap-1">
                  <motion.span 
                    className="text-2xl font-bold text-emerald-400"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                  >
                    5x
                  </motion.span>
                  <Zap className="w-3 h-3 text-yellow-400 mb-1.5" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// 4. 痛点与解决方案
const Solutions = () => {
  const painPoints = [
    { title: "资源分散", desc: "断层，缺整合商", icon: <Layers className="w-6 h-6" /> },
    { title: "重资产门槛", desc: "库存压力，资金风险", icon: <Database className="w-6 h-6" /> },
    { title: "决策随意", desc: "依赖经验，无数据", icon: <Brain className="w-6 h-6" /> },
    { title: "服务中断", desc: "人员变动，信息丢失", icon: <Users className="w-6 h-6" /> },
  ];

  const solutions = [
    { title: "角色中立化", desc: "临床立场，优选组合包", icon: <ShieldCheck className="w-6 h-6" /> },
    { title: "资产轻量化", desc: "共享库存，化解风险", icon: <Share2 className="w-6 h-6" /> },
    { title: "决策数字化", desc: "AI分析数据，精准策略", icon: <TrendingUp className="w-6 h-6" /> },
    { title: "业务永续化", desc: "双机长+GEM，资产数字化", icon: <Activity className="w-6 h-6" /> },
  ];

  return (
    <section id="痛点解决" className="py-20 relative z-10 bg-slate-900/50">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-white mb-4">打破行业壁垒 <span className="text-emerald-500">重构价值链条</span></h2>
          <p className="text-slate-400">直击传统医疗器械流通痛点，提供全维度的数字化解法</p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Pain Points */}
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-rose-400 mb-6 flex items-center gap-2">
              <span className="w-2 h-8 bg-rose-500 rounded-full"></span> 
              传统模式痛点
            </h3>
            {painPoints.map((item, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="bg-slate-800/50 p-6 rounded-xl border border-slate-700 hover:border-rose-500/30 transition-colors flex items-center gap-4"
              >
                <div className="bg-slate-900 p-3 rounded-lg text-rose-400">{item.icon}</div>
                <div>
                  <h4 className="text-white font-medium">{item.title}</h4>
                  <p className="text-sm text-slate-400">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Solutions */}
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-emerald-400 mb-6 flex items-center gap-2">
              <span className="w-2 h-8 bg-emerald-500 rounded-full"></span> 
              WellRise 解决方案
            </h3>
            {solutions.map((item, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="bg-emerald-900/10 p-6 rounded-xl border border-emerald-500/20 hover:bg-emerald-900/20 hover:border-emerald-500/50 transition-all flex items-center gap-4 group"
              >
                <div className="bg-emerald-900/30 p-3 rounded-lg text-emerald-400 group-hover:scale-110 transition-transform">{item.icon}</div>
                <div>
                  <h4 className="text-white font-medium">{item.title}</h4>
                  <p className="text-sm text-slate-400">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// 5. 核心模式：铁三角
const CoreModel = () => {
  return (
    <section id="核心模式" className="py-24 relative z-10 overflow-hidden">
      <div className="container mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center gap-16">
          <div className="md:w-1/2">
            <h2 className="text-4xl font-bold text-white mb-10">
              "众行远"<br/>
              <span className="text-emerald-400">高效三螺旋协作机制</span>
            </h2>
            {/* 删除了之前的段落，将内容整合进下方三点 */}
            
            <div className="space-y-8">
              {[
                { 
                  role: "MP 市场合伙人", 
                  desc: "临床需求破局者。独创铁三角合伙制，精准解决行业资源分散痛点。",
                  color: "border-cyan-500"
                },
                { 
                  role: "OD 运营总监", 
                  desc: "方案落地深耕者。通过双机长制护航，确保全周期专业服务永不掉线。",
                  color: "border-emerald-500"
                },
                { 
                  role: "B-BP 经营分析 BP", 
                  desc: "智慧枢纽。依托数字化平台监控数据，实现机制透明、多劳多得与风险共担。",
                  color: "border-purple-500"
                }
              ].map((item, i) => (
                <div key={i} className={`pl-6 border-l-4 ${item.color} py-1 transition-all hover:bg-white/5 rounded-r-lg group`}>
                  <h4 className="text-white font-bold text-xl mb-2 group-hover:text-emerald-300 transition-colors">{item.role}</h4>
                  <p className="text-slate-300 text-base leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="md:w-1/2 flex justify-center relative">
            {/* 模拟铁三角图示 */}
            <div className="relative w-[300px] h-[300px] md:w-[400px] md:h-[400px]">
              {/* 背景旋转环 */}
              <motion.div 
                animate={{ rotate: 360 }}
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 border-2 border-slate-700/50 rounded-full"
              />
              
              {/* 核心三角 SVG */}
              <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full overflow-visible drop-shadow-[0_0_15px_rgba(16,185,129,0.3)]">
                <motion.path
                  d="M50 15 L85 75 L15 75 Z"
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="0.5"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  transition={{ duration: 2 }}
                />
                {/* 节点 */}
                <circle cx="50" cy="15" r="8" fill="#0f172a" stroke="#06b6d4" strokeWidth="2" className="animate-pulse" />
                <circle cx="85" cy="75" r="8" fill="#0f172a" stroke="#10b981" strokeWidth="2" className="animate-pulse" />
                <circle cx="15" cy="75" r="8" fill="#0f172a" stroke="#a855f7" strokeWidth="2" className="animate-pulse" />
                
                {/* 交互线条 */}
                <path d="M50 15 L50 45" stroke="#334155" strokeDasharray="2 2" />
                <path d="M85 75 L60 60" stroke="#334155" strokeDasharray="2 2" />
                <path d="M15 75 L40 60" stroke="#334155" strokeDasharray="2 2" />
              </svg>

              {/* 标签 */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-4 bg-cyan-900/80 px-3 py-1 rounded text-cyan-300 text-xs font-bold border border-cyan-500/50 backdrop-blur-sm">
                MP 市场
              </div>
              <div className="absolute bottom-[20%] right-0 translate-x-4 bg-emerald-900/80 px-3 py-1 rounded text-emerald-300 text-xs font-bold border border-emerald-500/50 backdrop-blur-sm">
                OD 运营
              </div>
              <div className="absolute bottom-[20%] left-0 -translate-x-4 bg-purple-900/80 px-3 py-1 rounded text-purple-300 text-xs font-bold border border-purple-500/50 backdrop-blur-sm">
                BP 枢纽
              </div>
              
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/3 text-center">
                <div className="text-white font-bold text-xl tracking-widest">GEM</div>
                <div className="text-[10px] text-emerald-500 uppercase">销售决策系统</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// 6. 科技引擎：GEM + AI
const TechStack = () => {
  return (
    <section id="科技引擎" className="py-20 bg-slate-950 relative z-10">
      <div className="container mx-auto px-6 text-center">
        <h2 className="text-3xl font-bold text-white mb-16">
          数字化赋能 <span className="bg-gradient-to-r from-emerald-400 to-teal-500 text-transparent bg-clip-text">GEM系统 & AI智能体</span>
        </h2>

        <div className="grid md:grid-cols-2 gap-8">
          {/* 左侧：GEM */}
          <motion.div 
            whileHover={{ scale: 1.02 }}
            className="group relative bg-slate-900 rounded-2xl p-8 border border-slate-800 overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="relative z-10 flex flex-col h-full items-start text-left">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center mb-6">
                <Database className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">GEM 销售决策系统</h3>
              <p className="text-slate-400 mb-6">消除信息孤岛，实现信息100%对称。实时处理反馈，自动匹配解决方案。</p>
              <ul className="space-y-3 mt-auto w-full">
                <li className="flex justify-between text-sm border-b border-slate-800 pb-2">
                  <span className="text-slate-300">数据分析</span>
                  <span className="text-emerald-500">Real-time</span>
                </li>
                <li className="flex justify-between text-sm border-b border-slate-800 pb-2">
                  <span className="text-slate-300">决策支持</span>
                  <span className="text-emerald-500">AI Driven</span>
                </li>
              </ul>
            </div>
          </motion.div>

          {/* 右侧：AI Agents */}
          <div className="grid grid-rows-2 gap-6">
            <motion.div 
              whileHover={{ x: 5 }}
              className="bg-slate-900 p-6 rounded-xl border border-slate-800 flex items-center gap-4 relative overflow-hidden group"
            >
              <div className="absolute right-0 top-0 w-24 h-24 bg-cyan-500/10 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
              <div className="p-3 bg-cyan-900/30 rounded-lg text-cyan-400">
                <Target className="w-6 h-6" />
              </div>
              <div className="text-left">
                <h4 className="text-white font-bold">产品策略智能体</h4>
                <p className="text-sm text-slate-400">分析 VBP 数据，输出话术建议</p>
              </div>
            </motion.div>

            <motion.div 
              whileHover={{ x: 5 }}
              className="bg-slate-900 p-6 rounded-xl border border-slate-800 flex items-center gap-4 relative overflow-hidden group"
            >
               <div className="absolute right-0 top-0 w-24 h-24 bg-purple-500/10 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
              <div className="p-3 bg-purple-900/30 rounded-lg text-purple-400">
                <Cpu className="w-6 h-6" />
              </div>
              <div className="text-left">
                <h4 className="text-white font-bold">库存共享引擎</h4>
                <p className="text-sm text-slate-400">动态测算周转率，自动优化备货</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

// 7. 投资亮点卡片
const InvestmentHighlights = () => {
  const highlights = [
    { label: "模式稀缺性", sub: "高壁垒，强复制", color: "from-blue-500 to-cyan-500" },
    { label: "科技护城河", sub: "AI驱动，效能倍增", color: "from-emerald-500 to-teal-500" },
    { label: "资产安全", sub: "沉淀数字资产", color: "from-indigo-500 to-purple-500" },
    { label: "生态扩展性", sub: "稳健基石，无限联动", color: "from-rose-500 to-orange-500" },
  ];

  return (
    <section id="投资价值" className="py-24 relative z-10">
      <div className="container mx-auto px-6">
        <h2 className="text-3xl font-bold text-white text-center mb-12">未来已来 <span className="text-emerald-400">投资亮点</span></h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {highlights.map((item, index) => (
            <motion.div
              key={index}
              whileHover={{ y: -10 }}
              className="bg-slate-800/40 border border-slate-700 p-6 rounded-2xl text-center backdrop-blur-sm group cursor-pointer"
            >
              <div className={`w-12 h-1 bg-gradient-to-r ${item.color} mx-auto mb-4 rounded-full group-hover:w-full transition-all duration-500`}></div>
              <h3 className="text-white font-bold text-lg mb-2">{item.label}</h3>
              <p className="text-slate-400 text-sm">{item.sub}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// 8. 页脚
const Footer = () => (
  <footer className="bg-slate-950 border-t border-slate-900 py-12 relative z-10">
    <div className="container mx-auto px-6 text-center">
      <div className="flex items-center justify-center gap-2 mb-8">
        {/* Footer Logo 替换为心电图样式 */}
        <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center text-white font-bold">
           <Activity className="w-5 h-5 stroke-[3px]" />
        </div>
        <span className="text-xl font-bold text-white">广东沃瑞医疗科技有限公司</span>
      </div>
      <p className="text-slate-500 mb-8 max-w-2xl mx-auto">
        Leading the Future of Healthcare, Together. <br/>
        全球领先临床方案提供者，以需求为原点，精准高效配置资源。
      </p>
      <div className="flex justify-center gap-6 text-sm text-slate-600">
        <span>© 2024 GuangDong WellRise MedTech Co., Ltd.</span>
        <a href="#" className="hover:text-emerald-400">Privacy Policy</a>
        <a href="#" className="hover:text-emerald-400">Terms of Service</a>
      </div>
    </div>
  </footer>
);

// Main Layout
const WellRiseApp = () => {
  return (
    <div className="bg-slate-950 min-h-screen text-slate-200 font-sans selection:bg-emerald-500/30 selection:text-emerald-200 overflow-x-hidden">
      <BackgroundGrid />
      <Navbar />
      <main>
        <Hero />
        <Solutions />
        <CoreModel />
        <TechStack />
        <InvestmentHighlights />
      </main>
      <Footer />
    </div>
  );
};

export default WellRiseApp;